<template>
  <div class="traceability-samples">
    <div class="simple-infographic-cards">
      <simple-infographic-card
        label="Total de amostras"
        :value="indicators.total ?? '...'"
        icon-src="/static/images/icon-samples.svg"
        highlight-color="#00B5CC"
      />

      <simple-infographic-card
        label="Aguardando retirada"
        :value="indicators.waiting ?? '...'"
        icon-src="/static/images/icon-traceability-event-AGUARDANDO_RETIRADA.svg"
        highlight-color="#F78E26"
      />

      <simple-infographic-card
        label="Em trânsito"
        :value="indicators.inTransfer ?? '...'"
        icon-src="/static/images/icon-traceability-event-EM_TRANSITO.svg"
        highlight-color="#52CC9C"
      />

      <simple-infographic-card
        label="Em trânsito entre NTOs"
        :value="indicators.inTransferBetweenNTO ?? '...'"
        icon-src="/static/images/icon-traceability-event-EM_TRANSITO_ENTRE_NTO.svg"
        highlight-color="#8AD4DF"
      />
    </div>

    <div class="traceability-samples-card">
      <div class="traceability-samples-main-content">
        <div>
          <div class="traceability-samples-main-content-title">
            NTOS Alvaro Apoio
          </div>

          <div class="traceability-samples-filter">
            <div class="traceability-samples-filter-title">
              Filtros
            </div>

            <div class="traceability-samples-filter-inputs">
              <autocomplete
                v-model="model.filter.state"
                :disabled="isStateControlDisabled"
                :options="stateOptions"
              />

              <autocomplete
                v-model="model.filter.nto"
                :disabled="isNTOControlDisabled"
                :options="ntoOptions"
              />
            </div>
          </div>

          <div class="map-zoom-buttons">
            <button
              class="no-style-btn map-zoom-button-out"
              @click="handleZoomOutMap"
            >
              <svg-loader src="/static/images/icon-zoom-out.svg" />
            </button>

            <button
              class="no-style-btn map-zoom-button-in"
              :disabled="!canZoomInMap"
              @click="handleZoomInMap"
            >
              <svg-loader src="/static/images/icon-zoom-in.svg" />
            </button>
          </div>
        </div>

        <div>
          <brazil-states-map
            ref="map"
            v-model="model.map.state"
            :enabled-units="statesWithResults"
            :markers="ntoMarkers"
            class="traceability-samples-map"
            @click-marker="handleClickMarker"
          />
        </div>
      </div>

      <div class="traceability-samples-sidebar">
        <spinner
          class="traceability-samples-sidebar-spinner"
          v-if="isFilterLoading"
        />

        <traceability-results
          v-else
          :results="results"
          :type="'amostra'"
          :details-level="resultsDetailsLevel"
          :updated-at="resultsUpdatedAt"
          @click-result="handleClickResult"
          @click-details="handleClickDetails"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  brazilianFederativeUnits,
  brazilianFederativeUnitsAcronyms,
} from '@/constants'

import moment from 'moment'
import { getLogisticSampleIndicators, getLogisticNTOsBySamples } from '@/api/traceability'
import notify from '@/api/notification'

import { TraceabilityDetailsLevel } from '@/enums'

import SimpleInfographicCard from '@/components/common/SimpleInfographicCard'
import BrazilStatesMap from '@/components/common/BrazilStatesMap'
import Autocomplete from '@/components/common/Autocomplete'
import Spinner from '@/components/common/Spinner'

import TraceabilityResults from './TraceabilityResults'

const STATE_EMPTY_OPTION = {
  label: 'Todos os estados',
  value: undefined
}

const NTO_EMPTY_OPTION = {
  label: 'Todos os NTOs',
  value: undefined
}

export default {
  name: 'LogisticTraceabilitySamples',
  components: {
    SimpleInfographicCard,
    BrazilStatesMap,
    Autocomplete,
    TraceabilityResults,
    Spinner,
  },
  data() {
    return {
      TraceabilityDetailsLevel,
      isComponentInitializing: true,
      model: {
        filter: {
          state: STATE_EMPTY_OPTION,
          nto: NTO_EMPTY_OPTION,
          startDate: this.$route.query.startDate,
          endDate: this.$route.query.endDate,
        },
        map: {
          state: undefined
        },
      },
      results: [],
      resultsWithNoFilter: [],
      resultsUpdatedAt: undefined,
      areFiltersOptionsReady: false,
      isDataTableLoading: false,
      isFilterLoading: false,
      indicators: {
        total: undefined,
        waiting: undefined,
        inTransfer: undefined,
        inTransferBetweenNTO: undefined,
      }
    }
  },
  computed: {
    dateRange() {
      return [this.$route.query.startDate, this.$route.query.endDate]
    },
    canZoomInMap() {
      return this.model.map.state === undefined
    },
    isStateControlDisabled() {
      return this.stateOptions.length === 1 || !this.areFiltersOptionsReady
    },
    isNTOControlDisabled() {
      return this.ntoOptions.length === 1 || !this.areFiltersOptionsReady
    },
    stateOptions() {
      return [
        STATE_EMPTY_OPTION,
        ...brazilianFederativeUnitsAcronyms
          .map((unit, index) => ({
            label: brazilianFederativeUnits[index],
            value: unit
          }))
          .filter(({ value }) => this.statesWithResults.includes(value))
      ]
    },
    ntoOptions() {
      return [
        NTO_EMPTY_OPTION,
        ...this.resultsWithNoFilter
          .map(({ destination, idDestination, state }) => ({
            label: destination,
            value: idDestination,
            state,
          }))
          .filter(({ state }) => {
            if (this.model.filter.state !== STATE_EMPTY_OPTION) {
              return state === this.model.filter.state?.value
            }

            return true
          })
      ]
    },
    statesWithResults() {
      return brazilianFederativeUnitsAcronyms
        .filter((unit) => this.resultsWithNoFilter.some(result => result.state === unit))
    },
    resultsDetailsLevel() {
      if (this.model.filter.nto && this.model.filter.nto !== NTO_EMPTY_OPTION) {
        return TraceabilityDetailsLevel.State
      }

      if (this.model.filter.state && this.model.filter.state !== STATE_EMPTY_OPTION) {
        return TraceabilityDetailsLevel.State
      }

      return TraceabilityDetailsLevel.Country
    },
    ntoMarkers() {
      return this.results.map(result => ({
        id: result.idDestination,
        label: result.destination,
        x: result.mapFrontPosition.longitude,
        y: result.mapFrontPosition.latitude
      }))
    }
  },
  watch: {
    'model.filter.state': {
      handler(value) {
        if (value !== this.model.map.state) {
          this.model.map.state = value?.value
          
          if(!this.model.map.state){
            this.model.filter.nto = NTO_EMPTY_OPTION
          }
        }
      },
    },
    'model.map.state': {
      handler(value) {
        if (value !== this.model.filter.state) {
          this.model.filter.state = this.stateOptions.find(option => option.value === value) || this.stateOptionAll
        }
      },
    },
    'model.filter': {
      handler() {
        if (this.isComponentInitializing) return

        this.$nextTick(() => {
          const query = this.getQueryParamsByFilter()

          if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
            this.$router.replace({
              path: this.$route.path,
              query,
            }).catch(() => {})

            this.$nextTick(() => {
              this.handleFilter()
            })
          }
        })
      },
      deep: true,
    },
    'model.filter.nto': {
      handler(value) {
        if (value && value !== NTO_EMPTY_OPTION) {
          const stateOption = this.stateOptions.find(option => option.value === value.state)

          if (stateOption !== this.model.filter.state) {
            this.model.filter.state = stateOption
          }
        }
      },
    },
    '$route.query.startDate': {
      handler() {
        this.model.filter.startDate = this.$route.query.startDate
      },
    },
    '$route.query.endDate': {
      handler() {
        this.model.filter.endDate = this.$route.query.endDate
      },
    },
    'dateRange': {
      handler(newValue, oldValue) {
        if (
          !this.isComponentInitializing &&
          (
            newValue[0] !== oldValue?.[0] ||
            newValue[1] !== oldValue?.[1]
          ) &&
          this.model.filter.startDate &&
          this.model.filter.endDate
        ) {
          this.updateResultsWithNoFilter()
          this.updateIndicators()
          this.handleFilter()
        }
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    await this.updateResultsWithNoFilter()
    this.setFiltersWithQueryParams()
    this.updateIndicators()
    this.handleFilter()

    this.$nextTick(() => {
      this.isComponentInitializing = false
    })
  },
  methods: {
    handleZoomInMap() {
      this.$refs.map.zoomIn();
    },
    handleZoomOutMap() {
      if (this.model.filter.nto !== NTO_EMPTY_OPTION) {
        this.model.filter.nto = NTO_EMPTY_OPTION
        this.model.filter.state = STATE_EMPTY_OPTION
      } else if (this.model.filter.state !== STATE_EMPTY_OPTION) {
        this.model.filter.state = STATE_EMPTY_OPTION
      } else {
        this.$refs.map.zoomOut();
      }
    },
    async handleFilter() {
      this.isFilterLoading = true
      this.results = []
      this.results = await this.fetchNTOs()
      this.resultsUpdatedAt = new Date()
      this.isFilterLoading = false
    },
    handleClickResult(result) {
      switch (this.resultsDetailsLevel) {
        case TraceabilityDetailsLevel.Country:
          const stateOption = this.stateOptions.find(option => option.value === result.state);
          const ntoOption = this.ntoOptions.find(value => value.value === result.idDestination);

          if (ntoOption) {
            this.model.filter.nto = ntoOption;
          }

          if (stateOption) {
            this.model.filter.state = stateOption
          }

          break
      }
    },
    handleClickDetails(nto) {
      this.goToNTOSearch(nto)
    },
    handleClickMarker(nto) {
      this.goToNTOSearch(nto)
    },
    goToNTOSearch(nto) {
      this.$router.push({
        path: '/traceability/batches',
        query: {
          destinationId: nto.id ? nto.id : nto.idDestination
        },
      })
    },
    async fetchNTOs() {
      try {
        const response = await getLogisticNTOsBySamples(this.getFilterPayload())

        return response?.data ?? []
      } catch (error) {
        console.error('Error fetching logistic traceability NTOs', error)

        notify({
          title: 'Erro',
          message: 'Erro ao buscar NTOs com fluxo logístico.'
        })
      }

      return []
    },
    async fetchNTOsWithNoFilter() {
      try {
        const startDate = this.model.filter.startDate ?? moment(new Date()).subtract(7, 'days').format('YYYY-MM-DD')
        const endDate = this.model.filter.endDate ?? moment(new Date()).format('YYYY-MM-DD')
        const response = await getLogisticNTOsBySamples({
          startDate,
          endDate,
        })

        return response?.data ?? []
      } catch (error) {
        console.error('Error fetching logistic traceability NTOs with no filter', error)
      }

      return []
    },
    async fetchIndicators() {
      try {
        const response = await getLogisticSampleIndicators({
          startDate: this.model.filter.startDate,
          endDate: this.model.filter.endDate
        })

        return response?.data
      } catch (error) {
        console.error('Error fetching logistic traceability header data', error)
      }
    },
    getFilterPayload() {
      const filter = {}

      if (this.$route.query.state) {
        filter.state = this.$route.query.state
      }

      if (this.$route.query.nto) {
        filter.idDestination = this.$route.query.nto
      }

      if (this.$route.query.startDate) {
        filter.startDate = this.$route.query.startDate
      }

      if (this.$route.query.endDate) {
        filter.endDate = this.$route.query.endDate
      }

      return filter
    },
    getQueryParamsByFilter() {
      const query = {}

      if (this.model.filter.state?.value) {
        query.state = this.model.filter.state?.value
      }

      if (this.model.filter.nto?.value) {
        query.nto = this.model.filter.nto?.value
      }

      if (this.model.filter.startDate) {
        query.startDate = this.model.filter.startDate
      }

      if (this.model.filter.endDate) {
        query.endDate = this.model.filter.endDate
      }

      return query
    },
    setFiltersWithQueryParams() {
      const queryParamState = this.$route.query.state
      const queryParamNTO = this.$route.query.nto

      if (queryParamState && queryParamState !== this.model.filter.state?.value) {
        const stateOption = this.stateOptions.find(({ value }) => value === queryParamState)

        if (stateOption) {
          this.model.filter.state = stateOption
        }
      }

      if (queryParamNTO && queryParamNTO !== this.model.filter.nto?.value) {
        const ntoOption = this.ntoOptions.find(({ value }) => value === queryParamNTO)

        if (ntoOption) {
          this.model.filter.nto = ntoOption
        }
      }

      this.areFiltersOptionsReady = true
    },
    async updateResultsWithNoFilter() {
      this.resultsWithNoFilter = []
      this.resultsWithNoFilter = await this.fetchNTOsWithNoFilter()
    },
    async updateIndicators() {
      Object.keys(this.indicators).forEach(key => {
        this.indicators[key] = undefined
      })

      this.indicators = await this.fetchIndicators()
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.simple-infographic-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.traceability-samples {
  font-family: "Exo 2";

  &-card {
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #FFFFFF;
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  &-main-content {
    flex: 1;
    padding-top: 1rem;
    padding-bottom: 1rem;

    &-title {
      font-weight: 700;
      font-size: 1.125rem;
      color: #818181;
      margin-bottom: 0.5rem;
    }
  }

  &-filter {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;

    &-title {
      font-weight: 600;
      font-size: 0.75rem;
      text-transform: uppercase;
      color: #7F1A54;
      width: fit-content;
    }

    &-inputs {
      display: flex;
      gap: 0.5rem;
      flex: 1;
    }
  }

  &-map {
    display: flex;
    justify-content: center;
  }

  &-plan {
    color: transparent;
    width: auto;
    height: auto;
    min-width: auto;
    min-height: auto;
    margin-top: 1rem;
    margin-left: auto;
    margin-right: auto;
    display: block;
    max-width: 650px;
  }

  &-sidebar {
    border-radius: 0.375rem;
    background-color: #FFFFFF;
    box-shadow: 0px 4px 40px 0px #EC207226;
    width: 300px;

    &-spinner {
      margin-top: 1.5rem;
    }
  }

  .no-style-btn {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    outline: none;
    height: auto;
  }

  .map-zoom-buttons {
    display: flex;
    gap: 0.5rem;

    .md-icon {
      width: 20px;
      height: 20px;
      min-width: auto;
      min-height: auto;
    }
  }

  .map-zoom-button-in {
    color: $brand-primary;
    transition: transform 0.3s;

    &:active:not(:disabled) {
      transform: scale(0.9);
    }

    &:disabled {
      color: #919191;
      cursor: not-allowed;
    }
  }

  .map-zoom-button-out {
    color: $brand-primary;
    transition: transform 0.3s;

    &:active:not(:disabled) {
      transform: scale(0.9);
    }

    &:disabled {
      color: #919191;
      cursor: not-allowed;
    }
  }
}
</style>
